import type { ReactNode } from "react";
import { SetiIcon } from "./seti/seti";
import { create } from "zustand";

/**
 * Ícones de arquivo e pasta. Sem a extensão "Ícones de arquivo", cada arquivo tem o ícone neutro do
 * Kompil — o documento, em cinza, com a sigla da linguagem (JS, PY, {}…) — e as pastas só a seta.
 * Com a extensão, cada tipo ganha o seu ícone colorido — TypeScript, React, HTML, CSS, JSON,
 * Python, Docker, Git, testes… — e as pastas comuns (src, public, components, test…) ganham cor,
 * como os pacotes de ícones do VS Code.
 */
type Props = { className?: string };

function Svg({ className, children }: Props & { children: ReactNode }) {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

/** Ícone de arquivo padrão (sem a extensão de ícones). */
export function GenericFileIcon({ className }: Props) {
  return (
    <Svg className={className}>
      <path
        d="M4.2 1.5h4.9l3.4 3.4v9.6H4.2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
      <path d="M9 1.6v3.4h3.4" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    </Svg>
  );
}

const FONT = "Inter, 'Segoe UI', Arial, sans-serif";

function Letters({
  bg,
  fg = "#fff",
  text,
  size = 6.4,
  round = 2.6,
  className,
}: Props & { bg: string; fg?: string; text: string; size?: number; round?: number }) {
  return (
    <Svg className={className}>
      <rect x="1.2" y="1.2" width="13.6" height="13.6" rx={round} fill={bg} />
      <text
        x="8"
        y="8.15"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize={size}
        fontWeight="800"
        fontFamily={FONT}
        fill={fg}
        letterSpacing="-0.2"
      >
        {text}
      </text>
    </Svg>
  );
}

function Atom({ color, className }: Props & { color: string }) {
  return (
    <Svg className={className}>
      <g fill="none" stroke={color} strokeWidth="1.05">
        <ellipse cx="8" cy="8" rx="6.6" ry="2.55" />
        <ellipse cx="8" cy="8" rx="6.6" ry="2.55" transform="rotate(60 8 8)" />
        <ellipse cx="8" cy="8" rx="6.6" ry="2.55" transform="rotate(120 8 8)" />
      </g>
      <circle cx="8" cy="8" r="1.35" fill={color} />
    </Svg>
  );
}

function Shield({ color, className, children }: Props & { color: string; children: ReactNode }) {
  return (
    <Svg className={className}>
      <path d="M2.3 1.5h11.4l-1.05 11.3L8 14.6l-4.65-1.8z" fill={color} />
      <path d="M8 2.6v10.9l3.7-1.4.9-9.5z" fill="#fff" opacity="0.14" />
      {children}
    </Svg>
  );
}

function Doc({ color, className, children }: Props & { color: string; children?: ReactNode }) {
  return (
    <Svg className={className}>
      <path d="M3.6 1.3h5.6l3.2 3.2v10.2H3.6z" fill={color} />
      <path d="M9.2 1.3v3.2h3.2z" fill="#000" opacity="0.22" />
      {children}
    </Svg>
  );
}

const ICONS = {
  ts: (c?: string) => <Letters className={c} bg="#3178C6" text="TS" />,
  js: (c?: string) => <Letters className={c} bg="#F7DF1E" fg="#1a1a1a" text="JS" />,
  tsx: (c?: string) => <Atom className={c} color="#3B9EFF" />,
  jsx: (c?: string) => <Atom className={c} color="#22D3EE" />,
  html: (c?: string) => (
    <Shield className={c} color="#E44D26">
      <path
        d="M6.3 5.6 4.6 8l1.7 2.4M9.7 5.6 11.4 8l-1.7 2.4"
        fill="none"
        stroke="#fff"
        strokeWidth="1.15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Shield>
  ),
  css: (c?: string) => (
    <Shield className={c} color="#2965F1">
      <path
        d="M6.5 5.2 6 10.8M9.6 5.2l-.5 5.6M5 7h6.4M4.7 9h6.4"
        fill="none"
        stroke="#fff"
        strokeWidth="1.05"
        strokeLinecap="round"
      />
    </Shield>
  ),
  scss: (c?: string) => <Letters className={c} bg="#CD6799" text="S" size={9} round={7} />,
  json: (c?: string) => (
    <Svg className={c}>
      <path
        d="M5.4 2.3c-1.4 0-1.9.7-1.9 2v1.4c0 .9-.5 1.5-1.4 1.6v1.4c.9.1 1.4.7 1.4 1.6v1.4c0 1.3.5 2 1.9 2M10.6 2.3c1.4 0 1.9.7 1.9 2v1.4c0 .9.5 1.5 1.4 1.6v1.4c-.9.1-1.4.7-1.4 1.6v1.4c0 1.3-.5 2-1.9 2"
        fill="none"
        stroke="#F5B400"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="8" cy="8" r="1" fill="#F5B400" />
    </Svg>
  ),
  tsconfig: (c?: string) => (
    <Svg className={c}>
      <path
        d="M5.4 2.3c-1.4 0-1.9.7-1.9 2v1.4c0 .9-.5 1.5-1.4 1.6v1.4c.9.1 1.4.7 1.4 1.6v1.4c0 1.3.5 2 1.9 2M10.6 2.3c1.4 0 1.9.7 1.9 2v1.4c0 .9.5 1.5 1.4 1.6v1.4c-.9.1-1.4.7-1.4 1.6v1.4c0 1.3-.5 2-1.9 2"
        fill="none"
        stroke="#3178C6"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="8" cy="8" r="1" fill="#3178C6" />
    </Svg>
  ),
  markdown: (c?: string) => (
    <Svg className={c}>
      <rect x="1" y="3.3" width="14" height="9.4" rx="1.8" fill="none" stroke="#519ABA" strokeWidth="1.2" />
      <path
        d="M3.6 10.4V5.7l1.8 2.2 1.8-2.2v4.7M10.6 5.6v4.2M9 8.4l1.6 1.8 1.6-1.8"
        fill="none"
        stroke="#519ABA"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  ),
  python: (c?: string) => {
    const half =
      "M7.9 1.4c-2.7 0-3.1 1.2-3.1 2.5v1.6h3.3v.6H3.6c-1.3 0-2.2.9-2.2 2.8 0 2 .9 3 2.1 3h1.3V10c0-1.3 1.1-2.4 2.4-2.4h3.2c1.1 0 2-.9 2-2V3.9c0-1.2-1-2.5-4.5-2.5z";
    return (
      <Svg className={c}>
        <path d={half} fill="#3776AB" />
        <circle cx="6.2" cy="3.3" r=".6" fill="#fff" />
        <g transform="rotate(180 8 8)">
          <path d={half} fill="#FFD43B" />
          <circle cx="6.2" cy="3.3" r=".6" fill="#fff" />
        </g>
      </Svg>
    );
  },
  java: (c?: string) => (
    <Svg className={c}>
      <path
        d="M7.2 1.4c1.6 1.4-1.3 2.2.3 3.9M9.6 2.4c1.1 1-.9 1.5.2 2.7"
        fill="none"
        stroke="#E76F00"
        strokeWidth="1"
        strokeLinecap="round"
      />
      <path d="M3.2 7.2h8.4v3.1a3.2 3.2 0 0 1-3.2 3.2H6.4a3.2 3.2 0 0 1-3.2-3.2z" fill="#5382A1" />
      <path d="M11.6 8.1h.7a1.45 1.45 0 0 1 0 2.9h-.9" fill="none" stroke="#5382A1" strokeWidth="1.1" />
      <path d="M2.4 14.4h10" stroke="#5382A1" strokeWidth="1" strokeLinecap="round" />
    </Svg>
  ),
  go: (c?: string) => <Letters className={c} bg="#00ADD8" text="GO" size={6} />,
  rust: (c?: string) => <Letters className={c} bg="#DEA584" fg="#2b1a10" text="RS" size={6} round={7} />,
  php: (c?: string) => (
    <Svg className={c}>
      <ellipse cx="8" cy="8" rx="7" ry="4.6" fill="#777BB4" />
      <text
        x="8"
        y="8.3"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize="4.6"
        fontWeight="800"
        fontFamily={FONT}
        fill="#fff"
      >
        php
      </text>
    </Svg>
  ),
  ruby: (c?: string) => (
    <Svg className={c}>
      <path d="M4.2 2.2h7.6l2.7 3.6L8 14 1.5 5.8z" fill="#CC342D" />
      <path
        d="M1.5 5.8h13M4.2 2.2 8 5.8l3.8-3.6M8 5.8V14"
        fill="none"
        stroke="#fff"
        strokeWidth=".6"
        opacity=".55"
      />
    </Svg>
  ),
  c: (c?: string) => <Letters className={c} bg="#5C6BC0" text="C" size={9} round={7} />,
  cpp: (c?: string) => <Letters className={c} bg="#00599C" text="C++" size={5.4} round={7} />,
  csharp: (c?: string) => <Letters className={c} bg="#68217A" text="C#" size={6.2} round={7} />,
  shell: (c?: string) => (
    <Svg className={c}>
      <rect x="1.2" y="2.2" width="13.6" height="11.6" rx="2" fill="#2E3440" />
      <path
        d="M4 6l2.4 2L4 10M8 10.6h4"
        fill="none"
        stroke="#A3E635"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  ),
  yaml: (c?: string) => <Letters className={c} bg="#CB4A32" text="yml" size={5.2} />,
  config: (c?: string) => (
    <Svg className={c}>
      <circle cx="8" cy="8" r="2.2" fill="none" stroke="#8D99A6" strokeWidth="1.4" />
      <path
        d="M8 1.6v1.8M8 12.6v1.8M1.6 8h1.8M12.6 8h1.8M3.5 3.5l1.3 1.3M11.2 11.2l1.3 1.3M3.5 12.5l1.3-1.3M11.2 4.8l1.3-1.3"
        stroke="#8D99A6"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </Svg>
  ),
  xml: (c?: string) => (
    <Svg className={c}>
      <path
        d="M5.2 4.6 2.4 8l2.8 3.4M10.8 4.6 13.6 8l-2.8 3.4M9.1 3.6l-2.2 8.8"
        fill="none"
        stroke="#FF7043"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  ),
  database: (c?: string) => (
    <Svg className={c}>
      <ellipse cx="8" cy="3.8" rx="5.2" ry="2" fill="#F5A623" />
      <path
        d="M2.8 3.8v8.4c0 1.1 2.3 2 5.2 2s5.2-.9 5.2-2V3.8c0 1.1-2.3 2-5.2 2s-5.2-.9-5.2-2z"
        fill="#E08E0B"
      />
      <path d="M2.8 8c0 1.1 2.3 2 5.2 2s5.2-.9 5.2-2" fill="none" stroke="#F5A623" strokeWidth=".8" />
    </Svg>
  ),
  image: (c?: string) => (
    <Svg className={c}>
      <rect x="1.4" y="2.4" width="13.2" height="11.2" rx="1.8" fill="#26A69A" />
      <circle cx="5.3" cy="6" r="1.3" fill="#E0F2F1" />
      <path d="M2.4 12.6 6.3 8.4l2.6 2.8 1.6-1.7 3.1 3.1z" fill="#E0F2F1" />
    </Svg>
  ),
  svg: (c?: string) => (
    <Svg className={c}>
      <path
        d="M8 1.6 14.4 8 8 14.4 1.6 8z"
        fill="none"
        stroke="#FFB13B"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="2" fill="#FFB13B" />
    </Svg>
  ),
  docker: (c?: string) => (
    <Svg className={c}>
      <path
        d="M1.3 8.6h11.2c.8 0 1.5-.5 1.8-1.1.5.1 1 .4 1.2.8-.5.3-1.1.5-1.6.4-1 2.8-3.5 4.5-6.9 4.5-3.2 0-5.3-1.7-5.7-4.6z"
        fill="#2496ED"
      />
      {[
        [3, 6.6],
        [4.9, 6.6],
        [6.8, 6.6],
        [8.7, 6.6],
        [4.9, 4.7],
        [6.8, 4.7],
        [6.8, 2.8],
      ].map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="1.6" height="1.6" fill="#2496ED" />
      ))}
    </Svg>
  ),
  git: (c?: string) => (
    <Svg className={c}>
      <path d="M8 1.2 14.8 8 8 14.8 1.2 8z" fill="#F05032" />
      <path d="M6.2 4.3 9.7 7.8M8 6.1v4.5" stroke="#fff" strokeWidth="1.05" strokeLinecap="round" />
      <circle cx="8" cy="10.9" r="1.05" fill="#fff" />
      <circle cx="9.9" cy="8" r="1.05" fill="#fff" />
      <circle cx="6.2" cy="4.3" r="1.05" fill="#fff" />
    </Svg>
  ),
  node: (c?: string) => (
    <Svg className={c}>
      <path d="M8 1.2 13.9 4.6v6.8L8 14.8l-5.9-3.4V4.6z" fill="#7CB342" />
      <path
        d="M8 4.2 10.9 5.9v4.2L8 11.8 5.1 10.1V5.9z"
        fill="none"
        stroke="#fff"
        strokeWidth="1"
        opacity=".85"
      />
    </Svg>
  ),
  npm: (c?: string) => <Letters className={c} bg="#CB3837" text="npm" size={4.8} round={1.6} />,
  lock: (c?: string) => (
    <Svg className={c}>
      <rect x="3" y="7" width="10" height="7.5" rx="1.5" fill="#9AA5B1" />
      <path d="M5.2 7V5a2.8 2.8 0 0 1 5.6 0v2" fill="none" stroke="#9AA5B1" strokeWidth="1.4" />
      <circle cx="8" cy="10.6" r="1.1" fill="#fff" />
    </Svg>
  ),
  env: (c?: string) => (
    <Svg className={c}>
      <circle cx="5.6" cy="6.2" r="3.4" fill="none" stroke="#FBC02D" strokeWidth="1.5" />
      <path
        d="M8 8.6 13.8 14.4M11.6 12.2l1.4-1.4M10.1 10.7l1.1-1.1"
        stroke="#FBC02D"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </Svg>
  ),
  readme: (c?: string) => (
    <Svg className={c}>
      <circle cx="8" cy="8" r="6.6" fill="#42A5F5" />
      <path d="M8 7.2v4.2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="1" fill="#fff" />
    </Svg>
  ),
  license: (c?: string) => (
    <Doc className={c} color="#EF5350">
      <circle cx="8" cy="9.4" r="2" fill="#FFD54F" />
      <path d="M7 11.2 6.4 14l1.6-.9 1.6.9-.6-2.8" fill="#FFD54F" />
    </Doc>
  ),
  vite: (c?: string) => (
    <Svg className={c}>
      <path
        d="M14.4 2.4 8.4 14a.4.4 0 0 1-.7 0L1.6 2.4c-.2-.3.1-.7.4-.6L8 2.9l5.9-1.1c.4-.1.6.3.5.6z"
        fill="#A78BFA"
      />
      <path d="M10.6 1.4 6.1 2.3l-.3 5.4 1.4.3-.4 3.8 3.6-5.9-1.7-.3z" fill="#FFD62E" />
    </Svg>
  ),
  eslint: (c?: string) => (
    <Svg className={c}>
      <path d="M8 1.2 13.9 4.6v6.8L8 14.8l-5.9-3.4V4.6z" fill="none" stroke="#8080F2" strokeWidth="1.5" />
      <path d="M8 4.8 10.8 6.4v3.2L8 11.2 5.2 9.6V6.4z" fill="#8080F2" />
    </Svg>
  ),
  prettier: (c?: string) => (
    <Svg className={c}>
      {[
        ["#56B3B4", 3, 4],
        ["#EA5E5E", 5, 8],
        ["#F7BA3E", 7, 6],
        ["#BF85BF", 9, 9],
        ["#4D616E", 11, 5],
      ].map(([color, y, w]) => (
        <rect
          key={String(y)}
          x="2"
          y={Number(y)}
          width={Number(w)}
          height="1.4"
          rx=".7"
          fill={String(color)}
        />
      ))}
    </Svg>
  ),
  test: (c?: string) => (
    <Svg className={c}>
      <path
        d="M6.2 1.6h3.6M6.9 1.6v4.3L2.9 12.6c-.5.9.1 1.9 1.1 1.9h8c1 0 1.6-1 1.1-1.9L9.1 5.9V1.6"
        fill="none"
        stroke="#22C55E"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M4.3 10.3h7.4l1.2 2.3c.3.5-.1 1.1-.7 1.1H3.8c-.6 0-1-.6-.7-1.1z" fill="#22C55E" />
    </Svg>
  ),
  text: (c?: string) => (
    <Svg className={c}>
      <path
        d="M3 3.5h10M3 6.5h10M3 9.5h10M3 12.5h6"
        stroke="#9AA5B1"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </Svg>
  ),
  pdf: (c?: string) => (
    <Doc className={c} color="#E53935">
      <text x="8" y="11" textAnchor="middle" fontSize="4.2" fontWeight="800" fontFamily={FONT} fill="#fff">
        PDF
      </text>
    </Doc>
  ),
  zip: (c?: string) => (
    <Doc className={c} color="#B0814D">
      <path d="M7.6 3h.8M7.6 4.6h.8M7.6 6.2h.8M7.6 7.8h.8" stroke="#fff" strokeWidth="1" />
      <rect x="6.8" y="9" width="2.4" height="3" rx=".5" fill="#fff" />
    </Doc>
  ),
  vue: (c?: string) => (
    <Svg className={c}>
      <path d="M1.2 2.2h3.1L8 8.6l3.7-6.4h3.1L8 14z" fill="#41B883" />
      <path d="M4.3 2.2h2.3L8 4.6l1.4-2.4h2.3L8 8.6z" fill="#35495E" />
    </Svg>
  ),
  svelte: (c?: string) => <Letters className={c} bg="#FF3E00" text="S" size={9} round={7} />,
  dart: (c?: string) => <Letters className={c} bg="#0175C2" text="D" size={8.5} round={7} />,
  kotlin: (c?: string) => (
    <Svg className={c}>
      <path d="M1.5 1.5h13L8 8l6.5 6.5h-13z" fill="#7F52FF" />
    </Svg>
  ),
  swift: (c?: string) => <Letters className={c} bg="#F05138" text="SW" size={5.8} />,
  font: (c?: string) => <Letters className={c} bg="#EC407A" text="Aa" size={6.4} />,
  video: (c?: string) => (
    <Svg className={c}>
      <rect x="1.4" y="3" width="13.2" height="10" rx="2" fill="#FF7043" />
      <path d="M6.6 5.8v4.4L10.4 8z" fill="#fff" />
    </Svg>
  ),
  audio: (c?: string) => (
    <Svg className={c}>
      <path d="M6 11.6V3.2l7-1.4v8.2" fill="none" stroke="#EF5350" strokeWidth="1.3" />
      <circle cx="4.4" cy="11.8" r="1.8" fill="#EF5350" />
      <circle cx="11.4" cy="10.2" r="1.8" fill="#EF5350" />
    </Svg>
  ),
  file: (c?: string) => (
    <span className="text-fg-subtle">
      <GenericFileIcon className={c} />
    </span>
  ),
} satisfies Record<string, (className?: string) => ReactNode>;

type IconKey = keyof typeof ICONS;

const BY_NAME: Record<string, IconKey> = {
  "package.json": "node",
  "package-lock.json": "npm",
  "npm-shrinkwrap.json": "npm",
  ".npmrc": "npm",
  "pnpm-lock.yaml": "lock",
  "pnpm-workspace.yaml": "node",
  "yarn.lock": "lock",
  "bun.lockb": "lock",
  "readme.md": "readme",
  readme: "readme",
  license: "license",
  "license.md": "license",
  "license.txt": "license",
  ".gitignore": "git",
  ".gitattributes": "git",
  ".gitmodules": "git",
  ".gitkeep": "git",
  dockerfile: "docker",
  ".dockerignore": "docker",
  "docker-compose.yml": "docker",
  "docker-compose.yaml": "docker",
  "compose.yml": "docker",
  "compose.yaml": "docker",
  "tsconfig.json": "tsconfig",
  "jsconfig.json": "tsconfig",
  ".env": "env",
  ".prettierrc": "prettier",
  ".prettierrc.json": "prettier",
  "prettier.config.js": "prettier",
  ".prettierignore": "prettier",
  ".eslintrc": "eslint",
  ".eslintrc.json": "eslint",
  ".eslintrc.js": "eslint",
  "eslint.config.js": "eslint",
  "eslint.config.mjs": "eslint",
  "vite.config.ts": "vite",
  "vite.config.js": "vite",
  "vite.config.mjs": "vite",
  "vitest.config.ts": "vite",
  "requirements.txt": "python",
  "pyproject.toml": "python",
  pipfile: "python",
  "pom.xml": "java",
  "build.gradle": "java",
  "go.mod": "go",
  "go.sum": "go",
  "cargo.toml": "rust",
  "cargo.lock": "lock",
  makefile: "shell",
  ".editorconfig": "config",
};

const BY_EXT: Record<string, IconKey> = {
  ts: "ts",
  mts: "ts",
  cts: "ts",
  tsx: "tsx",
  js: "js",
  mjs: "js",
  cjs: "js",
  jsx: "jsx",
  html: "html",
  htm: "html",
  css: "css",
  less: "css",
  scss: "scss",
  sass: "scss",
  json: "json",
  jsonc: "json",
  json5: "json",
  md: "markdown",
  mdx: "markdown",
  py: "python",
  pyw: "python",
  ipynb: "python",
  java: "java",
  jar: "java",
  go: "go",
  rs: "rust",
  php: "php",
  rb: "ruby",
  c: "c",
  h: "c",
  cpp: "cpp",
  cc: "cpp",
  cxx: "cpp",
  hpp: "cpp",
  cs: "csharp",
  sh: "shell",
  bash: "shell",
  zsh: "shell",
  ps1: "shell",
  bat: "shell",
  cmd: "shell",
  yml: "yaml",
  yaml: "yaml",
  toml: "config",
  ini: "config",
  conf: "config",
  cfg: "config",
  xml: "xml",
  sql: "database",
  db: "database",
  sqlite: "database",
  prisma: "database",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  ico: "image",
  bmp: "image",
  avif: "image",
  svg: "svg",
  txt: "text",
  log: "text",
  csv: "text",
  pdf: "pdf",
  zip: "zip",
  tar: "zip",
  gz: "zip",
  rar: "zip",
  "7z": "zip",
  lock: "lock",
  vue: "vue",
  svelte: "svelte",
  dart: "dart",
  kt: "kotlin",
  kts: "kotlin",
  swift: "swift",
  woff: "font",
  woff2: "font",
  ttf: "font",
  otf: "font",
  mp4: "video",
  mov: "video",
  webm: "video",
  mp3: "audio",
  wav: "audio",
  ogg: "audio",
};

/** Qual ícone um arquivo ganha com a extensão de ícones. */
export function fileIconKey(name: string): IconKey {
  const lower = name.toLowerCase();
  if (/\.(test|spec)\.[a-z0-9]+$/.test(lower) || /^test_.+\.py$/.test(lower) || /tests?\.java$/.test(lower))
    return "test";
  if (BY_NAME[lower]) return BY_NAME[lower];
  if (lower.startsWith(".env.")) return "env";
  if (lower.startsWith("dockerfile.")) return "docker";
  const ext = lower.includes(".") ? lower.slice(lower.lastIndexOf(".") + 1) : "";
  return BY_EXT[ext] ?? "file";
}

/** Extensão "Ícones de arquivo" ligada (mantido em dia por `useColoredIcons`). */
export const useIconTheme = create<{ colored: boolean }>(() => ({ colored: false }));

/** Sigla de cada tipo no ícone neutro (sem a extensão de ícones). */
const TAGS: Record<IconKey, string> = {
  ts: "TS",
  js: "JS",
  tsx: "TSX",
  jsx: "JSX",
  html: "<>",
  css: "#",
  scss: "S",
  json: "{}",
  tsconfig: "TS",
  markdown: "MD",
  python: "PY",
  java: "JV",
  go: "GO",
  rust: "RS",
  php: "PHP",
  ruby: "RB",
  c: "C",
  cpp: "C++",
  csharp: "C#",
  shell: "$",
  yaml: "YML",
  config: "CFG",
  xml: "XML",
  database: "DB",
  image: "IMG",
  svg: "SVG",
  docker: "DK",
  git: "GIT",
  node: "N",
  npm: "NPM",
  lock: "LCK",
  env: "ENV",
  readme: "i",
  license: "©",
  vite: "V",
  eslint: "ES",
  prettier: "P",
  test: "T",
  text: "TXT",
  pdf: "PDF",
  zip: "ZIP",
  vue: "V",
  svelte: "S",
  dart: "DT",
  kotlin: "KT",
  swift: "SW",
  font: "Aa",
  video: "VID",
  audio: "AUD",
  file: "",
};

/** Ícone neutro do Kompil: o documento com a sigla do tipo, na cor do texto. */
function PlainTypeIcon({ tag, className }: Props & { tag: string }) {
  if (!tag) return <GenericFileIcon className={className} />;
  return (
    <Svg className={className}>
      <path
        d="M3.4 1.5h5.7l3.5 3.4v9.6H3.4z"
        fill="none"
        stroke="currentColor"
        strokeOpacity=".55"
        strokeWidth="1.05"
        strokeLinejoin="round"
      />
      <path
        d="M9 1.6v3.4h3.5"
        fill="none"
        stroke="currentColor"
        strokeOpacity=".55"
        strokeWidth="1.05"
        strokeLinejoin="round"
      />
      <text
        x="8"
        y="10.9"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize={tag.length >= 3 ? 3.9 : 4.9}
        fontWeight="800"
        fontFamily={FONT}
        fill="currentColor"
        letterSpacing="-0.15"
      >
        {tag}
      </text>
    </Svg>
  );
}

/**
 * Ícone do tipo do arquivo: o do VS Code (tema Seti, com as cores dele) por padrão, o neutro se a
 * pessoa desligou o plugin "Ícones de arquivo". `colored` força um dos dois (ex.: a loja).
 */
export function FileTypeIcon({
  name,
  className = "h-4 w-4",
  colored,
}: {
  name: string;
  className?: string;
  colored?: boolean;
}) {
  const themed = useIconTheme((s) => s.colored);
  const key = fileIconKey(name);
  if (colored ?? themed)
    return <SetiIcon name={name} className={className} fallback={ICONS[key](className)} />;
  return <PlainTypeIcon tag={TAGS[key]} className={`text-fg-muted ${className}`} />;
}

// ───────────── Pastas ─────────────

const FOLDER_COLORS: Array<[RegExp, string]> = [
  [/^(src|source|app)$/, "#26A69A"],
  [/^(components?|ui|widgets)$/, "#FFB300"],
  [/^(public|static|www)$/, "#29B6F6"],
  [/^(tests?|__tests__|spec|e2e|cypress)$/, "#66BB6A"],
  [/^(assets|images?|img|icons|media)$/, "#FFA726"],
  [/^(docs?|documentation)$/, "#42A5F5"],
  [/^node_modules$/, "#7CB342"],
  [/^\.(github|git|vscode|idea)$/, "#78909C"],
  [/^(dist|build|out|release|target|\.next)$/, "#EF5350"],
  [/^(config|configs|settings|infra)$/, "#7E57C2"],
  [/^(lib|libs|utils?|helpers|shared|common)$/, "#26C6DA"],
  [/^(styles?|css|scss|theme)$/, "#EC407A"],
  [/^(api|server|routes?|controllers?|services?|backend)$/, "#5C6BC0"],
  [/^(hooks|store|stores|state)$/, "#AB47BC"],
  [/^(pages|views|screens|layouts?)$/, "#FF7043"],
  [/^(scripts|bin|tools)$/, "#90A4AE"],
];

export function folderColor(name: string): string {
  const lower = name.toLowerCase();
  return FOLDER_COLORS.find(([re]) => re.test(lower))?.[1] ?? "#8D99A6";
}

/** Pasta colorida (fechada ou aberta) — só com a extensão de ícones. */
export function FolderTypeIcon({
  name,
  open = false,
  className = "h-4 w-4",
}: {
  name: string;
  open?: boolean;
  className?: string;
}) {
  const color = folderColor(name);
  return open ? (
    <Svg className={className}>
      <path
        d="M1.4 4c0-.8.6-1.4 1.4-1.4h3.3l1.5 1.6h5.5c.8 0 1.4.6 1.4 1.4v.8H4.4c-.7 0-1.2.4-1.4 1L1.4 11.6z"
        fill={color}
      />
      <path
        d="M3.1 7.1c.2-.6.7-1 1.3-1h10.1c.6 0 1 .6.8 1.2l-1.6 4.9c-.2.6-.7 1-1.3 1H2.3c-.6 0-1-.6-.8-1.2z"
        fill={color}
        opacity=".78"
      />
    </Svg>
  ) : (
    <Svg className={className}>
      <path
        d="M1.4 4c0-.8.6-1.4 1.4-1.4h3.3l1.5 1.6h5.5c.8 0 1.4.6 1.4 1.4v6.5c0 .8-.6 1.4-1.4 1.4H2.8c-.8 0-1.4-.6-1.4-1.4z"
        fill={color}
      />
      <path d="M1.4 5.6h13.2" stroke="#000" strokeOpacity=".16" strokeWidth=".8" />
    </Svg>
  );
}
